import { useEffect, useMemo, useState } from "react";
import { api, formatIDR, formatApiError } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, ChevronRight, Eye, Trash2, ArrowRight, Fingerprint, CalendarDays, X } from "lucide-react";

function defaultPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function firstAndLastOfMonth(period) {
  const [y, m] = period.split("-").map(Number);
  const first = `${y}-${String(m).padStart(2, "0")}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const last = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return { first, last };
}

export default function Payroll() {
  const [period, setPeriod] = useState(defaultPeriod());
  // const [actualWorkdays, setActualWorkdays] = useState(0);
  const _def = firstAndLastOfMonth(defaultPeriod());
  const [dateFrom, setDateFrom] = useState(_def.first);
  const [dateTo, setDateTo] = useState(_def.last);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeInfo, setRangeInfo] = useState(null);  // { matched_employees, total_days, unmatched_details }
  const [showDetailAbsen, setShowDetailAbsen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [preview, setPreview] = useState(null);
  // Per-employee overrides for the new Rincian table (Transport, Inc, THR, Pinjaman).
  // Diinisialisasi dari slip data saat preview selesai; user bisa override langsung.
  const [slipOverrides, setSlipOverrides] = useState({});
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [fpImporting, setFpImporting] = useState(false);
  const [fpResult, setFpResult] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);   // waktu terakhir auto-save
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [emp, rns] = await Promise.all([
        api.get("/employees"),
        api.get("/payroll/runs"),
      ]);
      const activeEmp = emp.data.filter((e) => e.active !== false);
      setEmployees(activeEmp);
      setRuns(rns.data);

      // Initialize default attendance
      const defaults = {};
      activeEmp.forEach((e) => {
        defaults[e.id] = { days_worked: 0, overtime_hours: 0, bonus: 0, deduction: 0, late_penalty_minutes: 0, early_leave_penalty_minutes: 0, wfh_minutes: 0 };
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Auto-adjust default date range saat period berubah (kecuali user sudah customize)
  useEffect(() => {
    const { first, last } = firstAndLastOfMonth(period);
    setDateFrom(first);
    setDateTo(last);
  }, [period]);

  const fetchRangeSummary = async (from, to) => {
    if (!from || !to) return;
    setRangeLoading(true);
    try {
      const { data } = await api.get(`/attendance/range/summary?date_from=${from}&date_to=${to}`);
      setRangeInfo(data);

      const attendanceFromSummary = Object.fromEntries(
        Object.entries(data.summary || {}).map(([employeeId, value]) => [
          employeeId,
          {
            days_worked: Number(value.days_worked || 0),
            overtime_hours: Number(value.overtime_hours || 0),
            late_penalty_minutes: Number(value.late_penalty_minutes || 0),
            early_leave_penalty_minutes: Number(value.early_leave_penalty_minutes || 0),
            wfh_minutes: Number(value.wfh_minutes || 0),
            bonus: 0,
            deduction: 0,
          },
        ])
      );

      setAttendance(attendanceFromSummary);

      const msg = data.matched_employees > 0
        ? `${data.matched_employees} karyawan ter-update dari rentang ${from} s/d ${to} (${data.total_days} hari-karyawan)`
        : `Rentang ${from} s/d ${to} — ${data.total_days} hari-karyawan (${data.unmatched_details?.length || 0} PIN belum ter-mapping)`;
      toast.success(msg);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal memuat rentang absensi");
    } finally {
      setRangeLoading(false);
    }
  };

  const updateAtt = (id, field, value) => {
    setAttendance((a) => ({ ...a, [id]: { ...a[id], [field]: Number(value) || 0 } }));
  };

  const runPreview = async () => {
    try {
      const { data } = await api.post("/payroll/preview", {
        period,
        attendance,
        overrides: slipOverrides
      });
      setPreview(data);
      // Init overrides tiap karyawan dari slip data (bisa di-edit user di tabel).
      // Preserve THR (dan pinjaman) yg sudah diinput user — sebab THR di backend digabung ke `bonus`
      // sehingga tidak bisa dibaca balik terpisah.
      setSlipOverrides((prev) => {
        const init = {};
        (data.slips || []).forEach((s) => {
          const existing = prev[s.employee_id] || {};
          init[s.employee_id] = {
            transport: Number(s.earnings?.tunjangan_transport || 0),
            inc_individu: Number(s.earnings?.insentif_individu || 0),
            inc_kolektif: Number(s.earnings?.insentif_kolektif || 0),
            inc_lain: Number(s.earnings?.insentif_lain || 0),
            thr: Number(existing.thr || 0),
            pinjaman: Number(s.deductions?.loan || 0),
          };
        });
        return init;
      });
      toast.success("Pratinjau dihitung");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal pratinjau");
    }
  };

  const runFinal = async () => {
    // Warning ringan (tidak memblokir) — user tetap bisa lanjut
    if (employees.length === 0) {
      toast.warning("⚠️ Tidak ada karyawan aktif — proses tetap dilanjutkan, tapi slip mungkin kosong");
    } else {
      const totalDays = Object.values(attendance).reduce((s, a) => s + Number(a?.days_worked || 0), 0);
      const totalOT = Object.values(attendance).reduce((s, a) => s + Number(a?.overtime_hours || 0), 0);
      if (totalDays === 0 && totalOT === 0) {
        toast.warning("⚠️ Semua Hari Hadir & Lembur = 0 — slip akan bernilai kosong. Proses tetap dilanjutkan.");
      }
    }
    if (!window.confirm(`Jalankan payroll untuk periode ${period}? Ini akan menggantikan data lama jika ada.`)) return;
    setRunning(true);
    try {
      const { data } = await api.post("/payroll/run", {
        period,
        attendance,
        overrides: slipOverrides
      });
      toast.success(`✅ Payroll ${period} berhasil dijalankan — ${data.employee_count} slip generated (Total Net Rp ${Number(data.total_net || 0).toLocaleString("id-ID")})`);
      navigate(`/payroll/${period}`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Kesalahan tidak diketahui";
      toast.error(`❌ Gagal menjalankan payroll: ${detail}`);
    } finally {
      setRunning(false);
    }
  };

  const deleteRun = async (p) => {
    if (!window.confirm(`Hapus payroll periode ${p}?`)) return;
    try {
      await api.delete(`/payroll/runs/${p}`);
      toast.success("Payroll dihapus");
      await load();
    } catch (err) {
      toast.error("Gagal menghapus");
    }
  };

  const onFingerprintImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFpImporting(true);
    setFpResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/attendance/import?period=${period}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const attendanceFromSummary = Object.fromEntries(
        Object.entries(data.summary || {}).map(([employeeId, value]) => [
          employeeId,
          {
            days_worked: Number(value.days_worked || 0),
            overtime_hours: Number(value.overtime_hours || 0),
            late_penalty_minutes: Number(value.late_penalty_minutes || 0),
            early_leave_penalty_minutes: Number(value.early_leave_penalty_minutes || 0),
            wfh_minutes: Number(value.wfh_minutes || 0),
            bonus: 0,
            deduction: 0,
          },
        ])
      );
      setAttendance(attendanceFromSummary);

      setFpResult(data);
      toast.success(`${data.matched_employees} karyawan ter-update dari ${data.total_scans} scan`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal import fingerprint");
    } finally {
      setFpImporting(false);
    }
  };

  const onResetAllAttendance = async () => {
    const step1 = window.prompt(
      "⚠️ RESET TOTAL ABSENSI ⚠️\n\n" +
      "Ini akan MENGHAPUS SEMUA data:\n" +
      "  • attendance_daily (semua hari, semua bulan, semua tahun)\n" +
      "  • attendance_imports (semua riwayat import)\n\n" +
      "AKSI INI TIDAK BISA DIBATALKAN.\n\n" +
      "Ketik persis: RESET SEMUA ABSENSI"
    );
    if (step1 !== "RESET SEMUA ABSENSI") {
      if (step1 !== null) toast.error("Konfirmasi tidak cocok. Reset dibatalkan.");
      return;
    }
    if (!window.confirm("Yakin lanjut reset TOTAL? Setelah reset Anda perlu upload ulang file Excel absensi.")) return;
    try {
      const { data } = await api.post("/attendance/reset-all?confirm=YES-RESET-ALL");
      toast.success(`Reset berhasil. Terhapus: ${data.deleted_daily} daily + ${data.deleted_imports} imports.`);
      setFpResult(null);
      setAttendance({});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal reset absensi");
    }
  };

  const totals = useMemo(() => {
    if (!preview) return null;
    return preview.totals;
  }, [preview]);

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4 pb-6 border-b border-zinc-200">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Operasi</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mt-1">Jalankan Payroll</h1>
          <p className="text-sm text-zinc-500 mt-1">Pilih periode, atur kehadiran/lembur, hitung otomatis lalu generate slip.</p>
        </div>
        <div className="flex items-end gap-3">
          <label className="block">
            <span className="block text-xs font-semibold text-zinc-900 uppercase tracking-wider mb-1.5">Periode</span>
            <input
              data-testid="payroll-period-select"
              type="month"
              value={period}
              onChange={(e) => { setPeriod(e.target.value); setPreview(null); }}
              className="rounded-none border border-zinc-300 px-3 py-2.5 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none font-mono"
            />
          </label>
          {/* <label className="block">
            <span className="block text-xs font-semibold text-zinc-900 uppercase tracking-wider mb-1.5">Jumlah Hari Kerja Efektif</span>
            <input
              data-testid="payroll-actual-workdays"
              type="number"
              min="1"
              max="31"
              value={actualWorkdays}
              onChange={(e) => { setActualWorkdays(e.target.value); setPreview(null); }}
              className="w-24 rounded-none border border-zinc-300 px-3 py-2.5 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none font-mono text-center"
            />
          </label> */}
          <button
            data-testid="preview-payroll-button"
            onClick={runPreview}
            title="Hitung pratinjau tanpa menyimpan"
            className="rounded-none border border-zinc-300 bg-white text-zinc-900 px-5 py-2.5 text-sm font-semibold hover:bg-zinc-50 inline-flex items-center gap-2"
          >
            <Calculator className="w-4 h-4" /> Hitung Pratinjau
          </button>
          <button
            data-testid="generate-payroll-button"
            onClick={runFinal}
            title="Simpan payroll & generate slip gaji untuk semua karyawan"
            className="rounded-none bg-[#002FA7] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#002FA7]/90 inline-flex items-center gap-2 cursor-pointer"
          >
            {running ? "Memproses…" : (<>Generate Slip <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </div>
      </div>

      {/* Autosave indicator */}
      {employees.length > 0 && !loading && (
        <div className="mt-2 flex items-center gap-3 text-[11px] text-zinc-500 font-mono">
          {lastSavedAt && (
            <span data-testid="autosave-indicator" className="inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-[#008A00] rounded-full animate-pulse" />
              Draft tersimpan otomatis · {new Date(lastSavedAt).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
        </div>
      )}

      {/* Helper hint saat tombol disabled atau kondisi tertentu */}
      {(() => {
        if (loading) return null;
        if (employees.length === 0) {
          return (
            <div data-testid="generate-hint-no-employees" className="mt-3 p-3 border-l-4 border-yellow-400 bg-yellow-50 text-sm">
              <b>Belum bisa Generate Slip:</b> Belum ada karyawan aktif. Silakan tambah karyawan di menu <Link to="/employees" className="text-[#002FA7] underline font-bold">Karyawan</Link>.
            </div>
          );
        }
        const totalDays = Object.values(attendance).reduce((s, a) => s + Number(a?.days_worked || 0), 0);
        const totalOT = Object.values(attendance).reduce((s, a) => s + Number(a?.overtime_hours || 0), 0);
        if (totalDays === 0 && totalOT === 0) {
          return (
            <div data-testid="generate-hint-no-attendance" className="mt-3 p-3 border-l-4 border-orange-400 bg-orange-50 text-sm">
              <b>⚠️ Absen belum diinput.</b> Semua karyawan memiliki Hari Hadir = 0 dan Lembur = 0. Silakan isi tabel kehadiran di bawah, atau import file finger, atau klik <b>Terapkan Rentang</b> untuk mengambil data absen dari periode terpilih.
            </div>
          );
        }
        return null;
      })()}

      {/* Range Absensi (Cross-Month Support) */}
      {employees.length > 0 && (
        <div className="mt-6 p-4 border border-zinc-200 bg-[#002FA7]/5 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 mr-2">
            <CalendarDays className="w-4 h-4 text-[#002FA7]" />
            <div className="text-xs font-bold uppercase tracking-widest text-[#002FA7]">Rentang Absensi (Fleksibel · Cross-Month)</div>
          </div>
          <label className="block">
            <span className="block text-[10px] font-bold text-zinc-700 uppercase tracking-wider mb-1">Dari Tanggal</span>
            <input
              data-testid="attendance-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-none border border-zinc-300 px-3 py-2 text-sm font-mono focus:border-[#002FA7] focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] font-bold text-zinc-700 uppercase tracking-wider mb-1">Sampai Tanggal</span>
            <input
              data-testid="attendance-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-none border border-zinc-300 px-3 py-2 text-sm font-mono focus:border-[#002FA7] focus:outline-none"
            />
          </label>
          <button
            data-testid="apply-attendance-range"
            onClick={() => fetchRangeSummary(dateFrom, dateTo)}
            disabled={rangeLoading || !dateFrom || !dateTo}
            className="rounded-none bg-[#002FA7] text-white px-5 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#002080] disabled:opacity-50"
          >
            {rangeLoading ? "Memuat…" : "Terapkan Rentang"}
          </button>
          <button
            data-testid="open-detail-absen"
            onClick={() => setShowDetailAbsen(true)}
            className="rounded-none border border-zinc-300 bg-white text-zinc-900 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-50 inline-flex items-center gap-2"
          >
            <Eye className="w-3.5 h-3.5" /> Detail Absen Harian
          </button>
          {rangeInfo && (
            <div className="text-[11px] font-mono text-zinc-600 ml-auto">
              {rangeInfo.matched_employees} ter-mapping · {rangeInfo.total_days} hari-karyawan · {rangeInfo.unmatched_details?.length || 0} PIN unmatched
            </div>
          )}
        </div>
      )}

      {employees.length === 0 && !loading && (
        <div className="mt-6 p-6 border border-zinc-200 bg-white">
          <div className="text-sm text-zinc-700">Belum ada karyawan aktif. Tambahkan karyawan terlebih dahulu.</div>
          <Link to="/employees" className="mt-3 inline-flex items-center gap-2 text-[#002FA7] font-semibold text-sm">
            Ke halaman Karyawan <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* Attendance & Adjustments */}
      {employees.length > 0 && (
        <div className="mt-6">
          <div className="flex items-end justify-between mb-2">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Input Kehadiran & Penyesuaian</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="reset-attendance-all-btn"
                onClick={onResetAllAttendance}
                className="rounded-none border border-[#E81123] bg-white text-[#E81123] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider hover:bg-[#E81123] hover:text-white inline-flex items-center gap-2"
                title="Hapus SEMUA data absensi (semua bulan, semua karyawan). Aksi tidak bisa dibatalkan."
              >
                <Trash2 className="w-3.5 h-3.5" />
                Reset Total Absensi
              </button>
              <label
                data-testid="import-fingerprint-label"
                className="rounded-none border border-zinc-300 bg-white text-zinc-900 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider hover:bg-zinc-50 inline-flex items-center gap-2 cursor-pointer"
              >
                <Fingerprint className="w-3.5 h-3.5" />
                {fpImporting ? "Mengimpor…" : "Import Fingerprint (XLSX/CSV)"}
                <input
                  data-testid="import-fingerprint-input"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={onFingerprintImport}
                  disabled={fpImporting}
                />
              </label>
            </div>
          </div>

          {fpResult && (
            <div className="mb-3 p-3 border border-zinc-200 bg-zinc-50">
              <div className="flex items-start justify-between">
                <div className="text-xs">
                  <div className="font-mono text-zinc-900">
                    <span className="font-semibold">{fpResult.matched_employees}</span> karyawan ter-update · <span className="font-semibold">{fpResult.total_scans}</span> scan diproses
                  </div>
                  {fpResult.unmatched_niks?.length > 0 && (
                    <div className="mt-1 text-[#E81123]">
                      PIN tidak cocok dengan NIK karyawan: <span className="font-mono">{fpResult.unmatched_niks.join(", ")}</span>
                    </div>
                  )}
                </div>
                <button onClick={() => setFpResult(null)} className="text-zinc-400 hover:text-zinc-700 text-xs uppercase tracking-widest font-semibold">tutup</button>
              </div>

              {fpResult.unmatched_details?.length > 0 && (
                <div className="mt-3 border border-zinc-200 bg-white overflow-x-auto">
                  <div className="px-3 py-2 bg-yellow-50 border-b border-zinc-200 text-[10px] uppercase tracking-widest font-bold text-yellow-900">
                    Hasil parsing PIN yang belum ter-mapping ke karyawan (silakan sesuaikan NIK karyawan)
                  </div>
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] font-bold text-zinc-600 uppercase tracking-widest">
                        <th className="px-3 py-2">PIN</th>
                        <th className="px-3 py-2">Nama (dari file)</th>
                        <th className="px-3 py-2 text-right">Hari Hadir</th>
                        <th className="px-3 py-2 text-right">Lembur (jam)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fpResult.unmatched_details.map((u) => (
                        <tr key={u.pin} data-testid={`unmatched-row-${u.pin}`} className="border-b border-zinc-100">
                          <td className="px-3 py-2 font-mono text-zinc-800">{u.pin}</td>
                          <td className="px-3 py-2 text-zinc-900">{u.name || <span className="text-zinc-400">—</span>}</td>
                          <td className="px-3 py-2 text-right font-mono">{u.days_worked}</td>
                          <td className="px-3 py-2 text-right font-mono">{u.overtime_hours}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="border border-zinc-200 bg-white overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200 text-[11px] font-bold text-zinc-600 uppercase tracking-widest">
                  <th className="px-4 py-3">No.</th>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">NIK</th>
                  <th className="px-4 py-3">Nama</th>
                  <th className="px-4 py-3 text-right">Gaji Pokok</th>
                  <th className="px-4 py-3 text-right">Hari Hadir</th>
                  <th className="px-4 py-3 text-right">Lembur (Jam)</th>
                  <th className="px-4 py-3 text-right text-[#002FA7]">Lembur (Rp)</th>
                  <th className="px-4 py-3 text-right" title="Total jam terlambat > 4 jam per hari (auto dari mesin finger). Input dalam jam; disimpan sebagai menit di backend.">Terlambat (Jam)</th>
                  <th className="px-4 py-3 text-right text-[#E81123]">Terlambat (Rp)</th>
                  <th className="px-4 py-3 text-right" title="Total jam pulang awal (setelah toleransi, auto dari izin portal approved + absen). Input dalam jam; disimpan sebagai menit di backend.">Pulang Awal (Jam)</th>
                  <th className="px-4 py-3 text-right text-[#F97316]">Pulang Awal (Rp)</th>
                  <th className="px-4 py-3 text-right">WFH (Jam)</th>
                  <th className="px-4 py-3 text-right text-emerald-700">WFH (Rp)</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, index) => {
                  const att = attendance[emp.id] || {};
                  const basic = Number(emp.basic_salary || 0);
                  // Formula pro-rata: ((Gaji Pokok / 26 Total Hari Kerja Efektif Sebulan) / 8 Jam Kerja Perhari) / 60
                  const wagePerMin = basic > 0 ? ((basic / 26) / 8) / 60 : 0;
                  const wagePerHour = wagePerMin * 60;
                  const otHours = Number(att.overtime_hours || 0);
                  const otRp = otHours * wagePerHour;
                  const lateMin = Number(att.late_penalty_minutes || 0);
                  const lateHours = lateMin / 60;
                  const lateRp = lateMin * wagePerMin;
                  const earlyMin = Number(att.early_leave_penalty_minutes || 0);
                  const earlyHours = earlyMin / 60;
                  const earlyRp = earlyMin * wagePerMin;
                  const wfhMin = Number(att.wfh_minutes || 0);
                  const wfhHours = wfhMin / 60;
                  const wfhRp = wfhMin * wagePerMin;
                  return (
                    <tr key={emp.id} className="border-b border-zinc-100 hover:bg-zinc-50/80">
                      <td className="px-4 py-2.5 font-mono text-xs text-zinc-700">{index + 1}.</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-zinc-700">{emp.nik}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-zinc-700">{emp.nik_new}</td>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-zinc-900">{emp.name}</div>
                        <div className="text-xs text-zinc-500">{emp.position} · {emp.ptkp_status}</div>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-right text-zinc-700">{formatIDR(basic)}</td>
                      <td className="px-4 py-2.5">
                        <input data-testid={`att-days-${emp.id}`} type="number" min="0" max="31" value={att.days_worked || 0}
                          onChange={(e) => updateAtt(emp.id, "days_worked", e.target.value)}
                          className="w-20 rounded-none border border-zinc-300 px-2 py-1 text-sm font-mono text-right focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none ml-auto block" />
                      </td>
                      <td className="px-4 py-2.5">
                        <input data-testid={`att-ot-${emp.id}`} type="number" min="0" step="0.5" value={att.overtime_hours || 0}
                          onChange={(e) => updateAtt(emp.id, "overtime_hours", e.target.value)}
                          className={`w-20 rounded-none border px-2 py-1 text-sm font-mono text-right focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none ml-auto block ${otHours > 0 ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]" : "border-zinc-300"}`} />
                      </td>
                      <td data-testid={`att-ot-rp-${emp.id}`} className={`px-4 py-2.5 font-mono text-right text-xs ${otRp > 0 ? "text-[#002FA7] font-bold" : "text-zinc-400"}`}>
                        {formatIDR(Math.round(otRp))}
                      </td>
                      <td className="px-4 py-2.5">
                        <input data-testid={`att-late-hours-${emp.id}`} type="number" min="0" step="0.5" value={lateHours ? Number(lateHours.toFixed(2)) : 0}
                          onChange={(e) => updateAtt(emp.id, "late_penalty_minutes", (Number(e.target.value) || 0) * 60)}
                          title="Total jam terlambat > 4 jam. Otomatis dari import absensi. Bila > 0, memicu potongan otomatis."
                          className={`w-24 rounded-none border px-2 py-1 text-sm font-mono text-right focus:border-red-400 focus:ring-1 focus:ring-red-400 focus:outline-none ml-auto block ${lateMin > 0 ? "border-red-400 bg-red-50 text-red-700" : "border-zinc-300"}`} />
                      </td>
                      <td data-testid={`att-late-rp-${emp.id}`} className={`px-4 py-2.5 font-mono text-right text-xs ${lateRp > 0 ? "text-[#E81123] font-bold" : "text-zinc-400"}`}>
                        {formatIDR(Math.round(lateRp))}
                      </td>
                      <td className="px-4 py-2.5">
                        <input data-testid={`att-early-hours-${emp.id}`} type="number" min="0" step="0.5" value={earlyHours ? Number(earlyHours.toFixed(2)) : 0}
                          onChange={(e) => updateAtt(emp.id, "early_leave_penalty_minutes", (Number(e.target.value) || 0) * 60)}
                          title="Total jam pulang awal (setelah toleransi). Otomatis dari izin portal approved yang punya scan absen. Bila > 0, memicu potongan otomatis."
                          className={`w-24 rounded-none border px-2 py-1 text-sm font-mono text-right focus:border-orange-400 focus:ring-1 focus:ring-orange-400 focus:outline-none ml-auto block ${earlyMin > 0 ? "border-orange-400 bg-orange-50 text-orange-700" : "border-zinc-300"}`} />
                      </td>
                      <td data-testid={`att-early-rp-${emp.id}`} className={`px-4 py-2.5 font-mono text-right text-xs ${earlyRp > 0 ? "text-[#F97316] font-bold" : "text-zinc-400"}`}>
                        {formatIDR(Math.round(earlyRp))}
                      </td>
                      <td className="px-4 py-2.5">
                        <input data-testid={`att-wfh-hours-${emp.id}`} type="number" min="0" step="0.5" value={wfhHours ? Number(wfhHours.toFixed(2)) : 0}
                          onChange={(e) => updateAtt(emp.id, "wfh_minutes", (Number(e.target.value) || 0) * 60)}
                          className={`w-24 rounded-none border px-2 py-1 text-sm font-mono text-right focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 focus:outline-none ml-auto block ${wfhMin > 0 ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-zinc-300"}`} />
                      </td>
                      <td data-testid={`att-wfh-rp-${emp.id}`} className={`px-4 py-2.5 font-mono text-right text-xs ${wfhRp > 0 ? "text-green-700 font-bold" : "text-zinc-400"}`}>
                        {formatIDR(Math.round(wfhRp))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-1 mt-2 text-[11px] text-zinc-500 font-mono">
            Kolom Rp otomatis dihitung dgn rumus pro-rata: <span className="font-bold">Upah/menit = ((Gaji Pokok / 26) / 8) / 60</span>. Lembur (Rp) = Jam × 60 × Upah/menit · Terlambat (Rp) = Jam × 60 × Upah/menit · Pulang Awal (Rp) = Jam × 60 × Upah/menit.
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="mt-8 fade-up">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            <PreviewCard label="Karyawan" value={totals.count} mono />
            <PreviewCard label="Total Bruto" value={formatIDR(totals.gross)} />
            {/* PPH 21 - For Future Use */}
            {/* <PreviewCard label="Total PPh 21" value={formatIDR(totals.pph21)} /> */}
            <PreviewCard label="Total Net (estimasi)" value={formatIDR(totals.net)} highlight />
          </div>

          <div className="mt-4 border border-zinc-200 bg-white">
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Rincian Pratinjau — Periode {preview.period}</div>
                <div className="text-[10px] text-zinc-400 mt-0.5 font-mono">Scroll ke samping untuk lihat semua kolom · Insentif/THR/Pinjaman dapat di-edit inline</div>
              </div>
              <div className="text-xs text-zinc-500 font-mono">Belum tersimpan</div>
            </div>
            <div className="overflow-x-auto">
              <table className="text-left text-sm" style={{ minWidth: "2100px" }} data-testid="rincian-pratinjau-table">
                <thead>
                  <tr className="bg-zinc-900 text-white text-[10px] font-bold uppercase tracking-widest">
                    <th className="px-3 py-3 text-left sticky left-0 z-10 bg-zinc-900 whitespace-nowrap">NIK</th>
                    <th className="px-3 py-3 text-left sticky left-[80px] z-10 bg-zinc-900 whitespace-nowrap min-w-[180px]">Nama</th>
                    <th className="px-3 py-3 text-right whitespace-nowrap">Gaji Pokok</th>
                    <th className="px-3 py-3 text-center whitespace-nowrap">Hari Hadir</th>
                    <th className="px-3 py-3 text-center whitespace-nowrap">Lembur<br/>(Jam)</th>
                    <th className="px-3 py-3 text-right bg-[#002FA7]/80 whitespace-nowrap">Lembur (Rp)</th>
                    <th className="px-3 py-3 text-center whitespace-nowrap">Terlambat<br/>(Jam)</th>
                    <th className="px-3 py-3 text-right bg-[#E81123]/80 whitespace-nowrap">Terlambat (Rp)</th>
                    <th className="px-3 py-3 text-center whitespace-nowrap">Pulang Awal<br/>(Jam)</th>
                    <th className="px-3 py-3 text-right bg-[#F97316]/80 whitespace-nowrap">Pulang Awal (Rp)</th>
                    <th className="px-3 py-3 text-center whitespace-nowrap">WFH<br/>(Jam)</th>
                    <th className="px-3 py-3 text-right bg-emerald-700 whitespace-nowrap">WFH (Rp)</th>
                    <th className="px-3 py-3 text-right whitespace-nowrap">Transport</th>
                    <th className="px-3 py-3 text-right whitespace-nowrap">Inc. Individu</th>
                    <th className="px-3 py-3 text-right whitespace-nowrap">Inc. Kolektif</th>
                    <th className="px-3 py-3 text-right whitespace-nowrap">Inc. Lain</th>
                    <th className="px-3 py-3 text-right whitespace-nowrap">THR</th>
                    <th className="px-3 py-3 text-right whitespace-nowrap text-[#F97316]">Pinjaman</th>
                    <th className="px-3 py-3 text-right bg-[#008A00]/90 whitespace-nowrap min-w-[160px]">Total Gaji</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.slips.map((s) => {
                    const ov = slipOverrides[s.employee_id] || { transport: 0, inc_individu: 0, inc_kolektif: 0, inc_lain: 0, thr: 0, pinjaman: 0, inch_wfh: 0 };
                    const gpokok = Number(s.earnings?.basic_salary || 0);
                    const overtimeRp = Number(s.earnings?.overtime || 0);
                    const lateRp = Number(s.deductions?.potongan_terlambat || 0);
                    const earlyRp = Number(s.deductions?.potongan_pulang_cepat || 0);
                    const wfhRp = Number(s.earnings?.tunjangan_wfh || 0);
                    const overtimeJam = Number(s.attendance?.overtime_hours || 0);
                    const lateJam = Number(s.attendance?.late_penalty_minutes || 0) / 60;
                    const earlyJam = Number(s.attendance?.early_leave_penalty_minutes || 0) / 60;
                    const wfhJam = Number(s.attendance?.wfh_minutes || 0) / 60;
                    const daysWorked = Number(s.attendance?.days_worked || 0);
                    // Rumus user: Total Gaji = (Gaji Pokok + Lembur Rp + WFH + Transport + Semua Inc + THR) − (Terlambat Rp + Pulang Awal Rp + Pinjaman)
                    const totalGaji =
                      gpokok + overtimeRp + wfhRp + Number(ov.transport || 0) +
                      Number(ov.inc_individu || 0) + Number(ov.inc_kolektif || 0) + Number(ov.inc_lain || 0) +
                      Number(ov.thr || 0) - lateRp - earlyRp - Number(ov.pinjaman || 0);
                    const inpCls = "w-24 border border-zinc-200 px-2 py-1 text-xs text-right font-mono focus:border-[#002FA7] focus:outline-none bg-zinc-50";
                    const setField = (field, v) => setSlipOverrides((prev) => ({ ...prev, [s.employee_id]: { ...prev[s.employee_id], [field]: Number(v) || 0 } }));
                    return (
                      <tr key={s.employee_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`preview-row-${s.employee_id}`}>
                        <td className="px-3 py-2.5 font-mono text-xs text-zinc-700 whitespace-nowrap sticky left-0 z-10 bg-white">{s.nik}</td>
                        <td className="px-3 py-2.5 sticky left-[80px] z-10 bg-white">
                          <div className="font-medium text-zinc-900 text-sm">{s.name}</div>
                          <div className="text-[10px] text-zinc-400 font-mono">{s.ptkp_status}</div>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-right text-zinc-900 text-xs whitespace-nowrap">{formatIDR(gpokok)}</td>
                        <td className="px-3 py-2.5 font-mono text-center text-zinc-700 text-xs">{daysWorked}</td>
                        <td className="px-3 py-2.5 font-mono text-center text-zinc-700 text-xs">{overtimeJam.toFixed(2)}</td>
                        <td className="px-3 py-2.5 font-mono text-right text-[#002FA7] text-xs whitespace-nowrap font-semibold">{formatIDR(overtimeRp)}</td>
                        <td className="px-3 py-2.5 font-mono text-center text-[#E81123] text-xs">{lateJam.toFixed(2)}</td>
                        <td className="px-3 py-2.5 font-mono text-right text-[#E81123] text-xs whitespace-nowrap font-semibold">{formatIDR(lateRp)}</td>
                        <td className="px-3 py-2.5 font-mono text-center text-[#F97316] text-xs">{earlyJam.toFixed(2)}</td>
                        <td className="px-3 py-2.5 font-mono text-right text-[#F97316] text-xs whitespace-nowrap font-semibold">{formatIDR(earlyRp)}</td>
                        <td className="px-3 py-2.5 font-mono text-center text-emerald-700 text-xs">{wfhJam.toFixed(2)}</td>
                        <td className="px-3 py-2.5 font-mono text-right text-emerald-700 text-xs whitespace-nowrap font-semibold">{formatIDR(wfhRp)}</td>
                        <td className="px-2 py-2 text-right"><input data-testid={`inp-transport-${s.employee_id}`} type="number" min="0" step="1000" value={ov.transport} onChange={(e) => setField("transport", e.target.value)} className={inpCls} /></td>
                        <td className="px-2 py-2 text-right"><input data-testid={`inp-inc-individu-${s.employee_id}`} type="number" min="0" step="1000" value={ov.inc_individu} onChange={(e) => setField("inc_individu", e.target.value)} className={inpCls} /></td>
                        <td className="px-2 py-2 text-right"><input data-testid={`inp-inc-kolektif-${s.employee_id}`} type="number" min="0" step="1000" value={ov.inc_kolektif} onChange={(e) => setField("inc_kolektif", e.target.value)} className={inpCls} /></td>
                        <td className="px-2 py-2 text-right"><input data-testid={`inp-inc-lain-${s.employee_id}`} type="number" min="0" step="1000" value={ov.inc_lain} onChange={(e) => setField("inc_lain", e.target.value)} className={inpCls} /></td>
                        <td className="px-2 py-2 text-right"><input data-testid={`inp-thr-${s.employee_id}`} type="number" min="0" step="1000" value={ov.thr} onChange={(e) => setField("thr", e.target.value)} className={inpCls} /></td>
                        <td className="px-2 py-2 text-right"><input data-testid={`inp-pinjaman-${s.employee_id}`} type="number" min="0" step="1000" value={ov.pinjaman} onChange={(e) => setField("pinjaman", e.target.value)} className={inpCls + " text-[#F97316] border-[#F97316]/40"} /></td>
                        <td className={`px-3 py-2.5 font-mono text-right text-sm font-bold whitespace-nowrap ${totalGaji >= 0 ? "text-[#008A00]" : "text-[#E81123]"} bg-[#008A00]/5`} data-testid={`total-gaji-${s.employee_id}`}>{formatIDR(totalGaji)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {(() => {
                    // Grand total per user's formula
                    let tGpokok = 0, tLemburRp = 0, tWFHRp = 0, tTransport = 0, tIncI = 0, tIncK = 0, tIncL = 0, tTHR = 0, tLate = 0, tEarly = 0, tPinj = 0;
                    preview.slips.forEach((s) => {
                      const ov = slipOverrides[s.employee_id] || {};
                      tGpokok += Number(s.earnings?.basic_salary || 0);
                      tLemburRp += Number(s.earnings?.overtime || 0);
                      tWFHRp += Number(s.earnings?.tunjangan_wfh || 0);
                      tLate += Number(s.deductions?.potongan_terlambat || 0);
                      tEarly += Number(s.deductions?.potongan_pulang_cepat || 0);
                      tTransport += Number(ov.transport || 0);
                      tIncI += Number(ov.inc_individu || 0);
                      tIncK += Number(ov.inc_kolektif || 0);
                      tIncL += Number(ov.inc_lain || 0);
                      tTHR += Number(ov.thr || 0);
                      tPinj += Number(ov.pinjaman || 0);
                    });
                    const grandTotal = tGpokok + tLemburRp + tWFHRp + tTransport + tIncI + tIncK + tIncL + tTHR - tLate - tEarly - tPinj;
                    return (
                      <tr className="bg-zinc-900 text-white border-t-2 border-zinc-900" data-testid="preview-footer-total">
                        <td colSpan={2} className="px-3 py-3 text-[10px] font-bold uppercase tracking-widest sticky left-0 z-10 bg-zinc-900">TOTAL ({preview.slips.length} kry)</td>
                        <td className="px-3 py-3 font-mono text-right text-xs whitespace-nowrap">{formatIDR(tGpokok)}</td>
                        <td className="px-3 py-3"></td>
                        <td className="px-3 py-3"></td>
                        <td className="px-3 py-3 font-mono text-right text-xs whitespace-nowrap text-[#93C5FD]">{formatIDR(tLemburRp)}</td>
                        <td className="px-3 py-3"></td>
                        <td className="px-3 py-3 font-mono text-right text-xs whitespace-nowrap text-[#FCA5A5]">{formatIDR(tLate)}</td>
                        <td className="px-3 py-3"></td>
                        <td className="px-3 py-3 font-mono text-right text-xs whitespace-nowrap text-[#FDBA74]">{formatIDR(tEarly)}</td>
                        <td className="px-3 py-3"></td>
                        <td className="px-3 py-3 font-mono text-right text-xs whitespace-nowrap text-[#6EE7B7]">{formatIDR(tWFHRp)}</td>
                        <td className="px-3 py-3 font-mono text-right text-xs whitespace-nowrap">{formatIDR(tTransport)}</td>
                        <td className="px-3 py-3 font-mono text-right text-xs whitespace-nowrap">{formatIDR(tIncI)}</td>
                        <td className="px-3 py-3 font-mono text-right text-xs whitespace-nowrap">{formatIDR(tIncK)}</td>
                        <td className="px-3 py-3 font-mono text-right text-xs whitespace-nowrap">{formatIDR(tIncL)}</td>
                        <td className="px-3 py-3 font-mono text-right text-xs whitespace-nowrap">{formatIDR(tTHR)}</td>
                        <td className="px-3 py-3 font-mono text-right text-xs whitespace-nowrap text-[#FDBA74]">{formatIDR(tPinj)}</td>
                        <td className="px-3 py-3 font-mono text-right text-base font-bold text-[#4ADE80] whitespace-nowrap" data-testid="preview-grand-total">{formatIDR(grandTotal)}</td>
                      </tr>
                    );
                  })()}
                </tfoot>
              </table>
            </div>
            <div className="px-4 py-2 border-t border-zinc-200 text-[10px] font-mono text-zinc-500 leading-relaxed">
              <b>Rumus Total Gaji</b> = (Gaji Pokok + Lembur Rp + WFH + Transport + Inc.Individu + Inc.Kolektif + Inc.Lain + THR) − (Terlambat Rp + Pulang Awal Rp + Pinjaman)
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div className="mt-10">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Riwayat Payroll</div>
        <div className="border border-zinc-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200 text-[11px] font-bold text-zinc-600 uppercase tracking-widest">
                <th className="px-4 py-3">Periode</th>
                <th className="px-4 py-3 text-right">Karyawan</th>
                <th className="px-4 py-3 text-right">Total Bruto</th>
                {/* PPH 21 - For Future Use */}
                {/* <th className="px-4 py-3 text-right">PPh 21</th> */}
                <th className="px-4 py-3 text-right">Total Net</th>
                <th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-400 font-mono text-xs">Belum ada periode yang dijalankan.</td></tr>
              )}
              {runs.map((r) => (
                <tr key={r.period} data-testid={`run-row-${r.period}`} className="border-b border-zinc-100 hover:bg-zinc-50/80">
                  <td className="px-4 py-3 font-mono text-zinc-900">{r.period}</td>
                  <td className="px-4 py-3 font-mono text-right text-zinc-700">{r.employee_count}</td>
                  <td className="px-4 py-3 font-mono text-right text-zinc-700">{formatIDR(r.total_gross)}</td>
                  {/* PPH 21 - For Future Use */}
                  {/* <td className="px-4 py-3 font-mono text-right text-zinc-700">{formatIDR(r.total_pph21)}</td> */}
                  <td className="px-4 py-3 font-mono text-right font-semibold text-zinc-900">{formatIDR(r.total_net)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/payroll/${r.period}`} className="p-1.5 hover:bg-zinc-100 text-zinc-700" title="Lihat detail">
                        <Eye className="w-4 h-4" />
                      </Link>
                      <button onClick={() => deleteRun(r.period)} className="p-1.5 hover:bg-[#E81123]/10 text-[#E81123]" title="Hapus">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Detail Absen Modal */}
      {showDetailAbsen && (
        <DetailAbsenModal
          initialFrom={dateFrom}
          initialTo={dateTo}
          onClose={() => setShowDetailAbsen(false)}
        />
      )}
    </div>
  );
}

function DetailAbsenModal({ initialFrom, initialTo, onClose }) {
  // Default: no filter → tampilkan SEMUA data yang sudah diimport
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [pin, setPin] = useState("");
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (params) => {
    setLoading(true);
    try {
      const p = params !== undefined ? params : { from, to, pin };
      const qs = new URLSearchParams();
      // Kalau kosong, kirim rentang super luas agar backend return semua
      qs.set("date_from", p.from || "2000-01-01");
      qs.set("date_to", p.to || "2099-12-31");
      if (p.pin) qs.set("pin", p.pin);
      const { data } = await api.get(`/attendance/daily/list?${qs.toString()}`);
      setItems(data.items || []);
      setMeta({
        count: data.count,
        total_overtime: data.total_overtime_hours,
        unique_dates: data.unique_dates,
        unique_pins: data.unique_pins,
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal memuat detail absen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load({ from: "", to: "", pin: "" }); /* eslint-disable-next-line */ }, []);

  // Quick actions
  const showAll = () => { setFrom(""); setTo(""); setPin(""); load({ from: "", to: "", pin: "" }); };
  const applyMonth = (period) => {
    if (!period) return;
    const [y, m] = period.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    const f = `${y}-${String(m).padStart(2, "0")}-01`;
    const t = `${y}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
    setFrom(f); setTo(t);
    load({ from: f, to: t, pin });
  };
  const applyCurrentMonth = () => {
    const d = new Date();
    applyMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-300 w-full max-w-6xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#002FA7]">
              <CalendarDays className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Detail Absensi Harian</div>
              <h3 className="font-bold text-zinc-900 text-lg">Semua Data Absensi ter-Import</h3>
            </div>
          </div>
          <button data-testid="detail-absen-close" onClick={onClose} className="p-2 hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>

        {/* Filter bar */}
        <div className="p-4 border-b border-zinc-200 bg-zinc-50 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="block text-[10px] font-bold text-zinc-700 uppercase tracking-wider mb-1">Dari</span>
              <input data-testid="detail-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none border border-zinc-300 px-3 py-2 text-sm font-mono focus:border-[#002FA7] focus:outline-none" />
            </label>
            <label className="block">
              <span className="block text-[10px] font-bold text-zinc-700 uppercase tracking-wider mb-1">Sampai</span>
              <input data-testid="detail-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none border border-zinc-300 px-3 py-2 text-sm font-mono focus:border-[#002FA7] focus:outline-none" />
            </label>
            <label className="block">
              <span className="block text-[10px] font-bold text-zinc-700 uppercase tracking-wider mb-1">PIN (opsional)</span>
              <input data-testid="detail-pin" type="text" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="1, 2, 10..." className="rounded-none border border-zinc-300 px-3 py-2 text-sm font-mono focus:border-[#002FA7] focus:outline-none w-32" />
            </label>
            <button data-testid="detail-apply" onClick={() => load()} disabled={loading} className="rounded-none bg-[#002FA7] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#002080] disabled:opacity-50">
              {loading ? "Memuat…" : "Terapkan Filter"}
            </button>
          </div>
          {/* Quick actions */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mr-1">Cepat:</span>
            <button
              data-testid="detail-show-all"
              onClick={showAll}
              className="rounded-none bg-[#008A00] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider hover:bg-[#006D00]"
            >
              Tampilkan Semua
            </button>
            <button
              data-testid="detail-current-month"
              onClick={applyCurrentMonth}
              className="rounded-none bg-white border border-zinc-300 text-zinc-900 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider hover:bg-zinc-50"
            >
              Bulan Ini
            </button>
            <label className="inline-flex items-center gap-1.5">
              <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Ke Bulan Spesifik:</span>
              <input data-testid="detail-month-quick" type="month" onChange={(e) => applyMonth(e.target.value)} className="rounded-none border border-zinc-300 px-2 py-1 text-xs font-mono focus:border-[#002FA7] focus:outline-none" />
            </label>
          </div>
          {meta && (
            <div className="flex flex-wrap gap-4 text-[11px] font-mono text-zinc-700 pt-1">
              <span><b className="text-zinc-900">{meta.count}</b> baris</span>
              <span><b className="text-zinc-900">{meta.unique_dates}</b> tanggal</span>
              <span><b className="text-zinc-900">{meta.unique_pins}</b> PIN</span>
              <span>Total lembur: <b className="text-[#002FA7]">{meta.total_overtime}</b> jam</span>
              {!from && !to && <span className="text-[#008A00] font-bold">· Menampilkan SEMUA data</span>}
              {(from || to) && <span className="text-[#002FA7]">· Filter: {from || "…"} s/d {to || "…"}</span>}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-zinc-100 border-b border-zinc-200">
              <tr className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest">
                <th className="px-3 py-2 w-12 text-center">No</th>
                <th className="px-3 py-2">Tanggal</th>
                <th className="px-3 py-2">PIN</th>
                <th className="px-3 py-2">Nama</th>
                <th className="px-3 py-2">NIK Karyawan</th>
                <th className="px-3 py-2 text-center">Jam Masuk</th>
                <th className="px-3 py-2 text-center">Jam Pulang</th>
                <th className="px-3 py-2 text-right">Lembur (jam)</th>
                <th className="px-3 py-2 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-400 font-mono text-xs">Memuat…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-400 font-mono text-xs">Tidak ada data pada rentang ini.</td></tr>
              )}
              {items.map((it, i) => {
                const matched = !!it.employee_id;
                return (
                  <tr key={`${it.pin}-${it.date}-${i}`} data-testid="detail-row" className={`border-b border-zinc-100 ${matched ? "" : "bg-yellow-50/50"}`}>
                    <td className="px-3 py-2 text-center font-mono text-[11px] text-zinc-500">{i + 1}</td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-900">{it.date}</td>
                    <td className="px-3 py-2 font-mono text-xs font-bold text-zinc-900">{it.pin}</td>
                    <td className="px-3 py-2 text-xs">{it.employee_name || <span className="text-zinc-300">—</span>}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">{it.employee_nik || <span className="text-zinc-300">—</span>}</td>
                    {it.is_wfh ? (
                      <td className="px-3 py-2 text-center font-mono text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">WFH</td>
                    ) : (
                      <td className="px-3 py-2 text-center font-mono text-xs">{it.in_time || <span className="text-zinc-300">—</span>}</td>
                    )}
                    {it.is_wfh ? (
                      <td className="px-3 py-2 text-center font-mono text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">WFH</td>
                    ) : (
                      <td className="px-3 py-2 text-center font-mono text-xs">{it.out_time || <span className="text-zinc-300">—</span>}</td>
                    )}
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      <div>{Number(it.overtime_hours || 0).toFixed(2)}</div>
                      {it.overtime_hours > 0.0 && (
                        <div className="text-[9px] text-[#002FA7] font-sans font-semibold">Izin Portal</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        {matched ? (
                          <>
                            {it.is_wfh ? (
                              <span className="inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 px-1 py-0.2 text-[8px] font-mono font-semibold">
                                WFH {Number(it.wfh_minutes / 60).toFixed(2)}j
                              </span>
                            ) : it.late_penalty_minutes > 0 ? (
                              <span className="inline-block bg-rose-50 text-rose-700 border border-rose-200 px-1 py-0.2 text-[8px] font-mono font-semibold">
                                Telat {it.late_penalty_minutes <= 60 ? `${it.late_penalty_minutes}m` : `${Number(it.late_penalty_minutes / 60).toFixed(2)}j`}
                              </span>
                            ) : (it.late_office_minutes || 0) > 0 ? (
                              <span className="inline-block bg-blue-50 text-blue-700 border border-blue-200 px-1 py-0.2 text-[8px] font-mono font-semibold">
                                Telat Kantor {it.late_office_minutes <= 60 ? `${it.late_office_minutes}m` : `${Number(it.late_office_minutes / 60).toFixed(2)}j`}
                              </span>
                            ) : (
                              <span className="inline-block bg-[#008A00]/15 text-[#008A00] border border-[#008A00]/30 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest">OK</span>
                            )}
                            {!it.is_wfh && Number(it.early_leave_penalty_minutes || 0) > 0 && (
                              <span className="inline-block bg-orange-50 text-orange-700 border border-orange-200 px-1 py-0.2 text-[8px] font-mono font-semibold">
                                Pulang Awal {it.early_leave_penalty_minutes <= 60 ? `${it.early_leave_penalty_minutes}m` : `${Number(it.early_leave_penalty_minutes / 60).toFixed(2)}j`}
                              </span>
                            )}
                            {!it.is_wfh && Number(it.early_leave_minutes || 0) > 0 && Number(it.early_leave_penalty_minutes || 0) <= 0 && (
                              <span className="inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 px-1 py-0.2 text-[8px] font-mono font-semibold">
                                Pulang Awal {it.early_leave_minutes}m (toleransi)
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="inline-block bg-yellow-400 text-yellow-900 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest">Unmatched</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end gap-2 p-3 border-t border-zinc-200 bg-zinc-50">
          <button data-testid="detail-close-btn" onClick={onClose} className="rounded-none bg-white border border-zinc-300 text-zinc-900 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-50">Tutup</button>
        </div>
      </div>
    </div>
  );
}

function PreviewCard({ label, value, highlight, mono }) {
  return (
    <div className={`bg-white p-5`}>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
      <div className={`font-mono mt-2 ${highlight ? "text-2xl font-semibold text-[#002FA7]" : "text-xl text-zinc-900"}`}>{value}</div>
    </div>
  );
}
